/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React from "react";
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  isComponentType,
  isKind,
} from "@backstage/plugin-catalog";
import { EntityCatalogGraphCard } from "@backstage/plugin-catalog-graph";
import { IssueFormOverview } from "@internal/plugin-jira-api";
import { Grid } from "@material-ui/core";
import { EntityWarningSwitch } from "./switches/EntityWarningSwitch";
import { AboutCard } from "./overrides/AboutCard";
import { EntityAdrContent } from "@backstage/plugin-adr";
import { isAdrAvailable } from "@backstage/plugin-adr-common";
import {
  EntityProvidingComponentsCard,
  EntityConsumingComponentsCard,
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityProvidedApisCard,
  EntityHasApisCard,
} from "@backstage/plugin-api-docs";
import { EntityTechdocsContent } from "@backstage/plugin-techdocs";
import { TechDocsAddons } from "@backstage/plugin-techdocs-react";
import { Mermaid } from "@internal/backstage-plugin-techdocs-addon-mermaid";
import { Entity } from "@backstage/catalog-model";
import {
  GithubIssuesCard,
  GithubIssuesPage,
} from "@backstage/plugin-github-issues";
import {
  EntityGroupProfileCard,
  EntityOwnershipCard,
  EntityMembersListCard,
  // EntityUserProfileCard,
} from "@backstage/plugin-org";
import { UserProfileCard } from "./overrides/User";
import {
  isConfluenceAvailable,
  EntityConfluenceContent,
} from "@internal/plugin-confluence-api";
import {
  isHoneybadgerAvailable,
  EntityHoneybadgerContent,
  EntityHoneybadgerOverviewCard,
} from "@internal/plugin-honeybadger";
import {
  isJiraAvailable,
  EntityJiraOverviewCard,
} from "@roadiehq/backstage-plugin-jira";
import {
  isGithubActionsAvailable,
  EntityGithubActionsContent,
} from "@backstage/plugin-github-actions";
import {
  isOpsgenieAvailable,
  EntityOpsgenieAlertsCard,
} from "@k-phoen/backstage-plugin-opsgenie";
import {
  isGithubInsightsAvailable,
  EntityGithubInsightsContent,
} from "@roadiehq/backstage-plugin-github-insights";
import {
  isGithubPullRequestsAvailable,
  EntityGithubPullRequestsContent,
} from "@roadiehq/backstage-plugin-github-pull-requests";
import { isSentryAvailable, EntitySentryCard } from "plugin-sentry";
import {
  codeDisplayAvailable,
  CodeDisplayPage,
  envVarsAvailable,
  EnvPage,
  featureFlagsAvailable,
  FeatureFlagPage,
} from "@internal/plugin-code-concerns";
import { routeDisplayAvailable, RouteDisplayPage } from "plugin-route-display";
import {
  isHarnessCiCdAvailable,
  EntityHarnessCiCdContent,
} from "@internal/harness-temporary";

const abstractionPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item lg={3} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item lg={3} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item lg={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item lg={3} xs={12}>
          <IssueFormOverview />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const apiPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item md={6}>
          <AboutCard />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
    {/* Definition */}
    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const defaultPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item lg={3} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item lg={3} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item lg={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item lg={3} xs={12}>
          <IssueFormOverview />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const docsPage = (
  <EntityLayout>
    {/* Docs */}
    <EntityLayout.Route path="/docs" title="Docs">
      <EntityTechdocsContent>
        {/* @ts-ignore */}
        <TechDocsAddons>
          <Mermaid />
        </TechDocsAddons>
      </EntityTechdocsContent>
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        <EntityWarningSwitch />
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        <EntityWarningSwitch />
        <Grid item md={12}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isJiraAvailable as (e: Entity) => boolean}>
            <Grid item xs={12}>
              <EntityJiraOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item xs={12}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Honeybadger */}
    <EntityLayout.Route
      path="/honeybadger"
      title="Honeybadger"
      if={isHoneybadgerAvailable}
    >
      <EntityHoneybadgerContent />
    </EntityLayout.Route>
    {/* Confluence */}
    <EntityLayout.Route
      path="/confluence"
      title="Confluence"
      if={isConfluenceAvailable}
    >
      <EntityConfluenceContent />
    </EntityLayout.Route>
    {/* Github Issues */}
    <EntityLayout.Route path="/github-issues-page" title="GitHub Issues">
      <GithubIssuesPage />
    </EntityLayout.Route>
  </EntityLayout>
);

const libraryPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item lg={3} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item lg={3} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item lg={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item lg={3} xs={12}>
          <IssueFormOverview />
        </Grid>
        <Grid item xs={12}>
          <EntityHasSubcomponentsCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isOpsgenieAvailable}>
            <Grid item xs={12}>
              <EntityOpsgenieAlertsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item xs={12}>
          <GithubIssuesCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isJiraAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityJiraOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        {/* Sentry */}
        <EntitySwitch>
          <EntitySwitch.Case if={isSentryAvailable}>
            <Grid item xs={12}>
              <EntitySentryCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        {/* Honeybadger */}
        <EntitySwitch>
          <EntitySwitch.Case if={isHoneybadgerAvailable}>
            <Grid item md={12}>
              <EntityHoneybadgerOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    {/* API */}
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
    {/* Harness */}
    <EntityLayout.Route
      if={isHarnessCiCdAvailable}
      path="/harness"
      title="Harness"
    >
      <EntityHarnessCiCdContent />
    </EntityLayout.Route>
    {/* Github Actions */}
    <EntityLayout.Route
      path="/github-actions"
      title="Github Actions"
      if={isGithubActionsAvailable}
    >
      <EntityGithubActionsContent />
    </EntityLayout.Route>
    {/* Pull Requests */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      if={isGithubPullRequestsAvailable as (entity: Entity) => boolean}
    >
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {/* Code Insights */}
    <EntityLayout.Route
      path="/code-insights"
      title="Code Insights"
      if={isGithubInsightsAvailable as (entity: Entity) => boolean}
    >
      <EntityGithubInsightsContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const modulePage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item md={3} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12} md={3}>
          <IssueFormOverview />
        </Grid>
        <Grid item xs={12}>
          <EntityHasSubcomponentsCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isOpsgenieAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityOpsgenieAlertsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isJiraAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityJiraOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isSentryAvailable}>
            <Grid item xs={12}>
              <EntitySentryCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isHoneybadgerAvailable}>
            <Grid item md={12}>
              <EntityHoneybadgerOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    {/* Code */}
    <EntityLayout.Route path="/code" title="Code" if={codeDisplayAvailable}>
      <CodeDisplayPage />
    </EntityLayout.Route>
    {/* Routes */}
    <EntityLayout.Route
      path="/routes"
      title="Routes"
      if={routeDisplayAvailable}
    >
      <RouteDisplayPage />
    </EntityLayout.Route>
    {/* Env Vars */}
    <EntityLayout.Route path="/env" title="ENV vars" if={envVarsAvailable}>
      <EnvPage />
    </EntityLayout.Route>
    {/* Feature Flags */}
    <EntityLayout.Route
      path="/feature-flags"
      title="Feature Flags"
      if={featureFlagsAvailable}
    >
      <FeatureFlagPage />
    </EntityLayout.Route>
    {/* API */}
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Dependencies */}
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Confluence */}
    <EntityLayout.Route
      path="/confluence"
      title="Confluence"
      if={isConfluenceAvailable}
    >
      <EntityConfluenceContent />
    </EntityLayout.Route>
    {/* Honeybadger */}
    <EntityLayout.Route
      path="/honeybadger"
      title="Honeybadger"
      if={isHoneybadgerAvailable}
    >
      <EntityHoneybadgerContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const monolithPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item md={4} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12}>
          <EntityHasSubcomponentsCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isOpsgenieAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityOpsgenieAlertsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isJiraAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityJiraOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isSentryAvailable}>
            <Grid item xs={12}>
              <EntitySentryCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isHoneybadgerAvailable}>
            <Grid item md={12}>
              <EntityHoneybadgerOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    {/* API */}
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Dependencies */}
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
    {/* Harness */}
    <EntityLayout.Route
      if={isHarnessCiCdAvailable}
      path="/harness"
      title="Harness"
    >
      <EntityHarnessCiCdContent />
    </EntityLayout.Route>
    {/* Code Insights */}
    <EntityLayout.Route
      path="/code-insights"
      title="Code Insights"
      if={isGithubInsightsAvailable as unknown as (entity: Entity) => boolean}
    >
      <EntityGithubInsightsContent />
    </EntityLayout.Route>
    {/* Github Actions */}
    <EntityLayout.Route
      path="/github-actions"
      title="Github Actions"
      if={isGithubActionsAvailable}
    >
      <EntityGithubActionsContent />
    </EntityLayout.Route>
    {/* Pull Requests */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      if={
        isGithubPullRequestsAvailable as unknown as (entity: Entity) => boolean
      }
    >
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {/* Honeybadger */}
    <EntityLayout.Route
      path="/honeybadger"
      title="Honeybadger"
      if={isHoneybadgerAvailable}
    >
      <EntityHoneybadgerContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const appPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        <EntityWarningSwitch />
        <Grid item md={6}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={12}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={12}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>

    {/* Code */}
    <EntityLayout.Route path="/code" title="Code" if={codeDisplayAvailable}>
      <CodeDisplayPage />
    </EntityLayout.Route>

    {/* Routes */}
    <EntityLayout.Route
      path="/routes"
      title="Routes"
      if={routeDisplayAvailable}
    >
      <RouteDisplayPage />
    </EntityLayout.Route>

    {/* Env Vars */}
    <EntityLayout.Route path="/env" title="ENV vars" if={envVarsAvailable}>
      <EnvPage />
    </EntityLayout.Route>

    {/* Feature Flags */}
    <EntityLayout.Route
      path="/feature-flags"
      title="Feature Flags"
      if={featureFlagsAvailable}
    >
      <FeatureFlagPage />
    </EntityLayout.Route>
    {/* API */}
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Dependencies */}
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Harness */}
    <EntityLayout.Route
      if={isHarnessCiCdAvailable}
      path="/harness"
      title="Harness"
    >
      <EntityHarnessCiCdContent />
    </EntityLayout.Route>
    {/* Github Actions */}
    <EntityLayout.Route
      path="/github-actions"
      title="Github Actions"
      if={isGithubActionsAvailable}
    >
      <EntityGithubActionsContent />
    </EntityLayout.Route>
    {/* Pull Requests */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      if={
        isGithubPullRequestsAvailable as unknown as (entity: Entity) => boolean
      }
    >
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {/* Honeybadger */}
    <EntityLayout.Route
      path="/honeybadger"
      title="Honeybadger"
      if={isHoneybadgerAvailable}
    >
      <EntityHoneybadgerContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const servicePage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        <EntityWarningSwitch />
        <Grid item md={6}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={12}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={12}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
    {/* Code */}
    <EntityLayout.Route path="/code" title="Code" if={codeDisplayAvailable}>
      <CodeDisplayPage />
    </EntityLayout.Route>
    {/* Routes */}
    <EntityLayout.Route
      path="/routes"
      title="Routes"
      if={routeDisplayAvailable}
    >
      <RouteDisplayPage />
    </EntityLayout.Route>
    {/* Env Vars */}
    <EntityLayout.Route path="/env" title="ENV vars" if={envVarsAvailable}>
      <EnvPage />
    </EntityLayout.Route>
    {/* Feature Flags */}
    <EntityLayout.Route
      path="/feature-flags"
      title="Feature Flags"
      if={featureFlagsAvailable}
    >
      <FeatureFlagPage />
    </EntityLayout.Route>
    {/* API */}
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Dependencies */}
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Harness */}
    <EntityLayout.Route
      if={isHarnessCiCdAvailable}
      path="/harness"
      title="Harness"
    >
      <EntityHarnessCiCdContent />
    </EntityLayout.Route>
    {/* Github Actions */}
    <EntityLayout.Route
      path="/github-actions"
      title="Github Actions"
      if={isGithubActionsAvailable}
    >
      <EntityGithubActionsContent />
    </EntityLayout.Route>
    {/* Pull Requests */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      if={
        isGithubPullRequestsAvailable as unknown as (entity: Entity) => boolean
      }
    >
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {/* Honeybadger */}
    <EntityLayout.Route
      path="/honeybadger"
      title="Honeybadger"
      if={isHoneybadgerAvailable}
    >
      <EntityHoneybadgerContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        <EntityWarningSwitch />
        <Grid item md={4}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={4}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={4}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const themePage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item md={3} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12} md={3}>
          <IssueFormOverview />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        <EntityWarningSwitch />
        <Grid item xs={12} md={6}>
          <UserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* Confluence */}
    <EntityLayout.Route
      path="/confluence"
      title="Confluence"
      if={isConfluenceAvailable}
    >
      <EntityConfluenceContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const websitePage = (
  <EntityLayout>
    {/* Overview */}
    <EntityLayout.Route path="/" title="Overview">
      <Grid container>
        <EntityWarningSwitch />
        <Grid item md={3} xs={12}>
          <AboutCard variant="gridItem" />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityCatalogGraphCard />
        </Grid>
        <Grid item md={3} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12} md={3}>
          <IssueFormOverview />
        </Grid>
        <Grid item xs={12}>
          <EntityHasSubcomponentsCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isOpsgenieAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityOpsgenieAlertsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case
            if={isJiraAvailable as (entity: Entity) => boolean}
          >
            <Grid item xs={12}>
              <EntityJiraOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isSentryAvailable}>
            <Grid item xs={12}>
              <EntitySentryCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isHoneybadgerAvailable}>
            <Grid item md={12}>
              <EntityHoneybadgerOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>
    {/* Dependencies */}
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* ADR */}
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
    {/* Harness */}
    <EntityLayout.Route
      if={isHarnessCiCdAvailable}
      path="/harness"
      title="Harness"
    >
      <EntityHarnessCiCdContent />
    </EntityLayout.Route>
    {/* Github Actions */}
    <EntityLayout.Route
      path="/github-actions"
      title="Github Actions"
      if={isGithubActionsAvailable}
    >
      <EntityGithubActionsContent />
    </EntityLayout.Route>
    {/* Pull Requests */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      if={
        isGithubPullRequestsAvailable as unknown as (entity: Entity) => boolean
      }
    >
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>
    {/* Code Insights */}
    <EntityLayout.Route
      path="/code-insights"
      title="Code Insights"
      if={isGithubInsightsAvailable as unknown as (entity: Entity) => boolean}
    >
      <EntityGithubInsightsContent />
    </EntityLayout.Route>
    {/* Honeybadger */}
    <EntityLayout.Route
      path="/honeybadger"
      title="Honeybadger"
      if={isHoneybadgerAvailable}
    >
      <EntityHoneybadgerContent />
    </EntityLayout.Route>
    {/* Confluence */}
    <EntityLayout.Route
      path="/confluence"
      title="Confluence"
      if={isConfluenceAvailable}
    >
      <EntityConfluenceContent />
    </EntityLayout.Route>
  </EntityLayout>
);

export const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType("app")} children={appPage} />

    <EntitySwitch.Case if={isComponentType("service")} children={servicePage} />

    <EntitySwitch.Case if={isComponentType("website")} children={websitePage} />

    <EntitySwitch.Case
      if={isComponentType("abstraction")}
      children={abstractionPage}
    />

    <EntitySwitch.Case if={isComponentType("module")} children={modulePage} />

    <EntitySwitch.Case if={isComponentType("library")} children={libraryPage} />

    <EntitySwitch.Case if={isComponentType("theme")} children={themePage} />

    <EntitySwitch.Case
      if={isComponentType("monolith")}
      children={monolithPage}
    />

    <EntitySwitch.Case>{defaultPage}</EntitySwitch.Case>
  </EntitySwitch>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind("component")} children={componentPage} />

    <EntitySwitch.Case if={isKind("api")} children={apiPage} />

    <EntitySwitch.Case if={isKind("group")} children={groupPage} />

    <EntitySwitch.Case if={isKind("user")} children={userPage} />

    <EntitySwitch.Case if={isKind("system")} children={systemPage} />

    <EntitySwitch.Case if={isKind("domain")} children={domainPage} />

    <EntitySwitch.Case if={isKind("documentation")} children={docsPage} />

    <EntitySwitch.Case>{defaultPage}</EntitySwitch.Case>
  </EntitySwitch>
);
